import React, { useState, useEffect, useRef, useContext } from "react";
import { useParams } from "react-router-dom";

import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import Select from "@material-ui/core/Select";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import MenuItem from "@material-ui/core/MenuItem";
import { Grid, ListItemText, Typography, makeStyles } from "@material-ui/core";

import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Autocomplete, {
  createFilterOptions,
} from "@material-ui/lab/Autocomplete";
import CircularProgress from "@material-ui/core/CircularProgress";

import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import toastError from "../../errors/toastError";
import useQueues from "../../hooks/useQueues";
import { AuthContext } from "../../context/Auth/AuthContext";
import { SettingsContext } from "../../context/Settings/SettingsContext";

const useStyles = makeStyles((theme) => ({
  maxWidth: {
    width: "100%",
  },
}));

const filterOptions = createFilterOptions({
  trim: true,
});

// Valor da fila/conexão na transferência em massa: cada chamado mantém a sua
const KEEP_EACH = "__keep__";

const TransferTicketModalCustom = ({ modalOpen, onClose, ticketid, ticket, bulkTickets }) => {
  // Transferência em massa (menu de contexto da lista): `ticket` é o primeiro da
  // seleção e só alimenta os rótulos; o PUT vai para cada um de `bulkTickets`.
  const isBulk = Array.isArray(bulkTickets) && bulkTickets.length > 1;
  const { ticketId: routeTicketId } = useParams();
  const [options, setOptions] = useState([]);
  const [queues, setQueues] = useState([]);
  const [allQueues, setAllQueues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedQueue, setSelectedQueue] = useState("");
  const [stickyTicketId, setStickyTicketId] = useState(null);
  const classes = useStyles();
  const { findAll: findAllQueues } = useQueues();
  const isMounted = useRef(true);
  const [whatsapps, setWhatsapps] = useState([]);
  const [whatsappsLoaded, setWhatsappsLoaded] = useState(false);
  const [selectedWhatsapp, setSelectedWhatsapp] = useState("");
  const { user } = useContext(AuthContext);
  const { companyId, whatsappId } = user;
  // Setting transferToOfflineUser desabilitada: quem está offline aparece na
  // lista, mas não pode ser escolhido (o backend recusa do mesmo jeito).
  const { getSetting } = useContext(SettingsContext);
  const blockOfflineUsers = getSetting("transferToOfflineUser") === "disabled";
  const isBlockedOffline = (option) =>
    blockOfflineUsers && !option?.online && option?.id !== user.id;
  const resolvedTicketId = stickyTicketId || ticketid || ticket?.id || ticket?.ticketId || routeTicketId;
  // O usuário é opcional em Aguardando (setting transferPendingTickets) e em
  // Atendendo. Com usuário o chamado vai para Atendendo com ele; sem, vai (ou
  // continua) em Aguardando na fila/conexão escolhida.
  const isPendingSource = ticket?.status === "pending";
  const isOpenSource = ticket?.status === "open";
  const isUserOptional = isPendingSource || isOpenSource;
  const selectedQueueValue = selectedQueue === null || selectedQueue === undefined ? "" : String(selectedQueue);
  const selectedWhatsappValue = selectedWhatsapp === null || selectedWhatsapp === undefined ? "" : String(selectedWhatsapp);
  const isQueueValueInOptions =
    selectedQueueValue === "" ||
    (isBulk && selectedQueueValue === KEEP_EACH) ||
    queues.some((q) => String(q.id) === selectedQueueValue);
  const isWhatsappValueInOptions =
    selectedWhatsappValue === "" ||
    (isBulk && selectedWhatsappValue === KEEP_EACH) ||
    whatsapps.some((w) => String(w.id) === selectedWhatsappValue);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Conexão pré-selecionada que não está nas opções (excluída) não pode seguir
  // escondida no submit: limpa e obriga a escolher outra.
  useEffect(() => {
    if (modalOpen && whatsappsLoaded && !isWhatsappValueInOptions) {
      setSelectedWhatsapp("");
    }
  }, [modalOpen, whatsappsLoaded, isWhatsappValueInOptions]);

  useEffect(() => {
    if (modalOpen) {
      const immediateId = ticketid || ticket?.id || ticket?.ticketId || routeTicketId;
      if (immediateId) {
        setStickyTicketId(immediateId);
      }
    } else {
      setStickyTicketId(null);
    }
  }, [modalOpen, ticketid, ticket, routeTicketId]);

  // Em massa, fila e conexão começam em "manter a de cada chamado": pré-carregar
  // as do primeiro moveria todos para a fila/conexão dele
  useEffect(() => {
    if (modalOpen && isBulk) {
      setSelectedQueue(KEEP_EACH);
      setSelectedWhatsapp(KEEP_EACH);
    }
  }, [modalOpen, isBulk]);

  // Pré-carregar valores do ticket quando modal abrir (apenas conexão/fila)
  useEffect(() => {
    if (!modalOpen || isBulk) {
      return;
    }

      const loadDefaults = async () => {
        try {
          if (!resolvedTicketId) return;

          const { data: ticketDetails } = await api.get(`/tickets/${resolvedTicketId}`);
          if (!isMounted.current || !ticketDetails) return;

          const queueFromTicket = ticketDetails.queueId ?? ticket?.queueId;
          const whatsappFromTicket = ticketDetails.whatsappId ?? ticket?.whatsappId;
          if (queueFromTicket !== undefined && queueFromTicket !== null) {
            setSelectedQueue(String(queueFromTicket));
          }
          if (whatsappFromTicket !== undefined && whatsappFromTicket !== null) {
            setSelectedWhatsapp(String(whatsappFromTicket));
          }
        } catch (err) {
          if (ticket?.queueId !== undefined && ticket?.queueId !== null) {
            setSelectedQueue(String(ticket.queueId));
          }
          if (ticket?.whatsappId !== undefined && ticket?.whatsappId !== null) {
            setSelectedWhatsapp(String(ticket.whatsappId));
          }
        }
      };

      loadDefaults();
  }, [modalOpen, isBulk, ticket, ticketid, resolvedTicketId]);

  useEffect(() => {
    if (!modalOpen) return;

    setLoading(true);
    const fetchUsers = async () => {
      try {
        const { data } = await api.get("/users/list");
        if (isMounted.current) {
          setOptions(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        toastError(err);
      } finally {
        if (isMounted.current) {
          setLoading(false);
        }
      }
    };

    fetchUsers();
  }, [modalOpen]);

  useEffect(() => {
    if (!modalOpen) return;

    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchContacts = async () => {
        api
          .get(`/whatsapp`, { params: { companyId, session: 0 } })
          .then(({ data }) => {
            setWhatsapps(data);
            setWhatsappsLoaded(true);
            // Só define valores padrão se estiver vazio e o ticket não tiver valor
            // explícito — e só se a conexão padrão do usuário ainda estiver na
            // lista (conexão excluída não vem do /whatsapp e não pode ser escolhida).
            const hasUserWhatsapp = (data || []).some((w) => String(w.id) === String(whatsappId));
            if (!selectedWhatsappValue && !ticket?.whatsappId && whatsappId !== null && whatsappId !== undefined && hasUserWhatsapp) {
              setSelectedWhatsapp(String(whatsappId))
            }
          });
      };

      if (!selectedQueueValue && !ticket?.queueId && user.queues.length === 1) {
        setSelectedQueue(String(user.queues[0].id))
      }
      fetchContacts();
      setLoading(false);
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [modalOpen, ticket, whatsappId, user.queues, companyId, selectedWhatsappValue, selectedQueueValue])

  useEffect(() => {
    if (isMounted.current) {
      const loadQueues = async () => {
        const list = await findAllQueues();
        setAllQueues(list);
        setQueues((prev) => (prev && prev.length > 0 ? prev : list));
      };
      loadQueues();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClose = () => {
    onClose();
    setSelectedUser(null);
    if (isBulk) {
      setSelectedQueue(KEEP_EACH);
      setSelectedWhatsapp(KEEP_EACH);
      return;
    }
    // Resetar para os valores do ticket ao fechar
    if (ticket?.queueId) {
      setSelectedQueue(String(ticket.queueId));
    } else {
      setSelectedQueue("");
    }
    if (ticket?.whatsappId) {
      setSelectedWhatsapp(String(ticket.whatsappId));
    } else {
      setSelectedWhatsapp("");
    }
  };

  // Mesmas regras para o chamado único e para cada chamado da transferência em massa
  const buildTransferPayload = (sourceStatus, queueId, targetWhatsappId) => {
    const data = {};

    // Sem usuário:
    // - Atendendo: volta para Aguardando (o backend tira o atendente).
    // - Aguardando: o status não vai no payload. O chamado continua pending,
    //   e reenviar "pending" reiniciaria o tempo de espera (queuedAt) no
    //   TicketTrackingLifecycleService.
    if (selectedUser) {
      data.userId = selectedUser.id;
      data.status = "open";
    } else if (sourceStatus === "open") {
      data.userId = null;
      data.status = "pending";
    }

    // Igual ao "Assumir": transferir um chamado em Aguardando é decisão humana
    // de roteamento, então o agente de IA sai (sem isto o backend recusa a ida
    // para Atendendo com ERR_PENDING_TICKET_WITH_AGENT).
    if (sourceStatus === "pending") {
      data.promptId = null;
    }

    // Permite queueId ser null (sem fila)
    data.queueId = queueId;

    if (targetWhatsappId) {
      data.whatsappId = Number(targetWhatsappId)
    }

    // Envia quem está fazendo a transferência para registro no histórico
    data.transferredBy = user.id;

    return data;
  };

  // Um PUT por chamado, em sequência (como as outras ações em massa do menu),
  // com um resumo no fim em vez de um erro por chamado
  const handleBulkTransfer = async () => {
    setLoading(true);
    let successCount = 0;
    let failCount = 0;
    let firstError = null;

    for (const t of bulkTickets) {
      // "Manter" manda a fila do próprio chamado explícita: o backend compara a
      // fila antiga com a enviada para escolher a mensagem de transferência
      const queueId =
        selectedQueueValue === KEEP_EACH
          ? (t.queueId ?? null)
          : selectedQueueValue
          ? Number(selectedQueueValue)
          : null;
      const targetWhatsappId =
        selectedWhatsappValue === KEEP_EACH ? t.whatsappId : selectedWhatsappValue;

      try {
        await api.put(`/tickets/${t.id}`, buildTransferPayload(t.status, queueId, targetWhatsappId));
        successCount++;
      } catch (err) {
        failCount++;
        if (!firstError) firstError = err;
      }
    }

    setLoading(false);

    if (successCount > 0) {
      toast.success(
        i18n.t("ticketContextMenu.bulkActionSuccess", {
          count: successCount,
          action: i18n.t("ticketContextMenu.actions.transfer"),
        })
      );
    }

    if (failCount > 0) {
      toast.error(i18n.t("ticketContextMenu.bulkActionFail", { count: failCount }));
      toastError(firstError);
    }

    onClose();
  };

  const handleSaveTicket = async (e) => {
    e.preventDefault();
    let finalTicketId = resolvedTicketId;

    // Fallback final: quando vier apenas UUID do ticket no contexto
    if (!finalTicketId && ticket?.uuid) {
      try {
        const { data: ticketFromUuid } = await api.get(`/tickets/u/${ticket.uuid}`);
        finalTicketId = ticketFromUuid?.id;
      } catch (err) {
        // mantém fluxo para erro amigável abaixo
      }
    }

    if (!finalTicketId) {
      console.error("[TransferTicketModalCustom] Não foi possível resolver ticketId", {
        ticketid,
        routeTicketId,
        ticket,
      });
      toastError(i18n.t("transferTicketModal.errors.invalidTicket"));
      return;
    }
    if (!selectedWhatsappValue || selectedWhatsappValue === "" || !isWhatsappValueInOptions) {
      toastError(i18n.t("transferTicketModal.selectConnection"));
      return;
    }
    if (!selectedUser && !isUserOptional) {
      toastError(i18n.t("transferTicketModal.errors.selectUser"));
      return;
    }
    if (isBulk) {
      await handleBulkTransfer();
      return;
    }
    setLoading(true);
    try {
      const data = buildTransferPayload(
        ticket?.status,
        selectedQueueValue ? Number(selectedQueueValue) : null,
        selectedWhatsappValue
      );

      console.log("[TransferTicketModalCustom] payload transferência:", {
        ticketid,
        resolvedTicketId,
        finalTicketId,
        data,
        selectedUser,
        selectedQueueValue,
        selectedWhatsappValue,
      });

      await api.put(`/tickets/${finalTicketId}`, data);

      // Fecha o modal imediatamente após sucesso para evitar overlay preso na tela
      setLoading(false);
      onClose();
    } catch (err) {
      setLoading(false);
      toastError(err);
    }
  };

  return (
    <Dialog open={modalOpen} onClose={handleClose} maxWidth="lg" scroll="paper">
      <form onSubmit={handleSaveTicket}>
        <DialogTitle id="form-dialog-title">
          {isBulk
            ? i18n.t("transferTicketModal.bulkTitle", { count: bulkTickets.length })
            : i18n.t("transferTicketModal.title")}
        </DialogTitle>
        <DialogContent dividers>
          <Autocomplete
            style={{ width: 300, marginBottom: 20 }}
            value={selectedUser}
            getOptionSelected={(option, value) => option?.id === value?.id}
            getOptionLabel={(option) => {
              if (typeof option === "string") return option;
              return option?.name || "";
            }}
            onChange={(e, newValue) => {
              setSelectedUser(newValue);
              if (newValue != null && Array.isArray(newValue.queues)) {
                setQueues(newValue.queues);

                if (isBulk) {
                  // Mesma ideia do chamado único: mantém a fila de cada chamado se
                  // todas estiverem entre as do usuário (ou sem fila); senão, a
                  // primeira fila dele
                  const userQueueIds = newValue.queues.map((q) => q.id);
                  const keepFits = bulkTickets.every(
                    (t) => !t.queueId || userQueueIds.includes(t.queueId)
                  );
                  if (keepFits) {
                    setSelectedQueue(KEEP_EACH);
                  } else if (newValue.queues.length > 0) {
                    setSelectedQueue(String(newValue.queues[0].id));
                  } else {
                    setSelectedQueue("");
                  }
                } else if (!ticket?.queueId) {
                  // Se o ticket original não tem fila, mantém sem fila
                  setSelectedQueue("");
                } else {
                  // Verificar se a fila atual do ticket está disponível para o usuário selecionado
                  const currentQueueAvailable = newValue.queues.some(q => q.id === ticket.queueId);
                  
                  if (currentQueueAvailable) {
                    // Mantém a fila atual do ticket
                    setSelectedQueue(String(ticket.queueId));
                  } else if (newValue.queues.length > 0) {
                    // Usa a primeira fila disponível do usuário
                    setSelectedQueue(String(newValue.queues[0].id));
                  } else {
                    // Usuário não tem filas, deixa vazio
                    setSelectedQueue("");
                  }
                }
              } else {
                setQueues(allQueues);
                // Mantém a fila atual do ticket (ou vazio se não tinha)
                setSelectedQueue(
                  isBulk ? KEEP_EACH : ticket?.queueId ? String(ticket.queueId) : ""
                );
              }
            }}
            options={options}
            getOptionDisabled={isBlockedOffline}
            renderOption={(option) =>
              isBlockedOffline(option)
                ? `${option.name} (${i18n.t("transferTicketModal.offline")})`
                : option.name
            }
            filterOptions={filterOptions}
            autoHighlight
            noOptionsText={i18n.t("transferTicketModal.noOptions")}
            loading={loading}
            renderInput={(params) => (
              <TextField
                {...params}
                label={i18n.t("transferTicketModal.fieldLabel")}
                helperText={
                  isPendingSource
                    ? i18n.t("transferTicketModal.pendingUserHelper")
                    : isOpenSource
                    ? i18n.t("transferTicketModal.openUserHelper")
                    : undefined
                }
                variant="outlined"
                autoFocus
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {loading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
          />
          <FormControl variant="outlined" className={classes.maxWidth}>
            <InputLabel>
              {i18n.t("transferTicketModal.fieldQueueLabel")}
            </InputLabel>
            <Select
              value={isQueueValueInOptions ? selectedQueueValue : ""}
              onChange={(e) => setSelectedQueue(e.target.value)}
              label={i18n.t("transferTicketModal.fieldQueuePlaceholder")}
            >
              {isBulk && (
                <MenuItem value={KEEP_EACH}>
                  <em>{i18n.t("transferTicketModal.keepQueue")}</em>
                </MenuItem>
              )}
              <MenuItem value="">
                <em>{i18n.t("transferTicketModal.noQueue")}</em>
              </MenuItem>
              {queues.map((queue) => (
                <MenuItem key={queue.id} value={String(queue.id)}>
                  {queue.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {/* CONEXAO */}
          <Grid container spacing={2} style={{marginTop: '15px'}}>
            <Grid xs={12} item>
              <Select
                required
                fullWidth
                displayEmpty
                variant="outlined"
                value={isWhatsappValueInOptions ? selectedWhatsappValue : ""}
                onChange={(e) => {
                  setSelectedWhatsapp(e.target.value)
                }}
                MenuProps={{
                  anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "left",
                  },
                  transformOrigin: {
                    vertical: "top",
                    horizontal: "left",
                  },
                  getContentAnchorEl: null,
                }}
                renderValue={() => {
                  if (selectedWhatsappValue === "") {
                    return i18n.t("transferTicketModal.selectConnection")
                  }
                  if (selectedWhatsappValue === KEEP_EACH) {
                    return i18n.t("transferTicketModal.keepConnection")
                  }
                  const whatsapp = whatsapps.find(w => String(w.id) === selectedWhatsappValue)
                  return whatsapp?.name || i18n.t("transferTicketModal.selectConnection")
                }}
              >
                {isBulk && (
                  <MenuItem dense value={KEEP_EACH}>
                    <ListItemText
                      primary={
                        <Typography component="span" style={{ fontSize: 14, marginLeft: "10px" }}>
                          <em>{i18n.t("transferTicketModal.keepConnection")}</em>
                        </Typography>
                      }
                    />
                  </MenuItem>
                )}
                {whatsapps?.length > 0 &&
                  whatsapps.map((whatsapp, key) => (
                    <MenuItem dense key={key} value={String(whatsapp.id)}>
                      <ListItemText
                        primary={
                          <>
                            {/* {IconChannel(whatsapp.channel)} */}
                            <Typography component="span" style={{ fontSize: 14, marginLeft: "10px", display: "inline-flex", alignItems: "center", lineHeight: "2" }}>
                              {whatsapp.name} &nbsp; <p className={(whatsapp.status) === 'CONNECTED' ? classes.online : classes.offline} >({whatsapp.status})</p>
                            </Typography>
                          </>
                        }
                      />
                    </MenuItem>
                  ))}
              </Select>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleClose}
            color="secondary"
            disabled={loading}
            variant="outlined"
          >
            {i18n.t("transferTicketModal.buttons.cancel")}
          </Button>
          <ButtonWithSpinner
            variant="contained"
            type="submit"
            color="primary"
            loading={loading}
            disabled={(!selectedUser && !isUserOptional) || !selectedWhatsappValue || selectedWhatsappValue === ""}
          >
            {i18n.t("transferTicketModal.buttons.ok")}
          </ButtonWithSpinner>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default TransferTicketModalCustom;
